import { Box, Button, Tab, Tabs } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { cloneDeep } from 'lodash';
import * as _ from 'lodash';
import type { SyntheticEvent } from 'react';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { useProgress } from '@/contexts/ProgressProvider';
import { AssetSource } from '@/enums';
import VnModalBody from '@/modals/components/VnModalBody';
import VnModalFooter from '@/modals/components/VnModalFooter';
import { useModalContext } from '@/modals/VNModalProvider';
import type { OU } from '@/models/Ou';
import type { ValidationError } from '@/models/ValidationError';
import { enhanceFetchedAsset } from '@/pages/assets/asset-listing/enhanceFetchedAsset';
import { generateNewAssetForm } from '@/pages/assets/clip-group-modal/toSaveClipGroupRequest';
import { FuelEntityType, FuelPortalService } from '@/services';
import { useAppDispatch } from '@/store';
import { AssetForm } from '@/store/forms';
import { SchemaType } from '@/store/forms/Asset-Editor/SchemaTypeOptions';
import { applicationDataSelector } from '@/store/slices/application-data';
import { assetEditorSelector, CreatAssetForm } from '@/store/slices/asset-editor';
import { fuelUserAccessSliceSelector } from '@/store/slices/fuelUserAccess';
import { startJob } from '@/store/slices/saveAssetJobsState/SaveAssetJobsState';
import useAppUtils from '@/utils/appUtils';
import clientUtils from '@/utils/clientUtils';
import { cliptoolUtils } from '@/utils/cliptoolUtils';

import VNAccessSettings from '../VNAccessSettings/VNAccessSettings';
import VideoForm from '../VNAssetUpload/forms/video';
import ContentClipsTab from './ContentClipsTab';
import DisplaySettingsForm from './DisplaySettingsForm';
import Preview from './Preview';
import { toSaveAssetPayload } from './toSaveAssetPayload';

const VNAssetEditor = ({ addToList = null }) => {
  const { assetForm } = useSelector(assetEditorSelector);
  const { permissions, fuelUserAccessData } = useSelector(fuelUserAccessSliceSelector);
  const { ouData } = useSelector(applicationDataSelector);
  const { onClose } = useModalContext();
  const [selectionType, setSelectionType] = useState('');
  const { alert } = useAppUtils();
  const [isInitialized, setIsInitialized] = useState(false);
  const [tab, setTab] = useState('details');
  const { loading, setProgress } = useProgress();
  const dispatch = useAppDispatch();
  const [isNew, setIsNew] = useState(false);
  const flags = useFlags();

  const methods = useForm({
    defaultValues: {} as AssetForm,
  });
  const { reset, getValues, setValue, watch } = methods;
  const isClipGroup = watch('source') === 'ClipGroup';

  const validateAssetForm = (ouData: OU, data: AssetForm) => {
    // console.log('validateAssetForm', { ouData, data });
    const errors: ValidationError[] = [];

    if (data.source == null) {
      errors.push({ fieldId: 'source', message: 'Source is required' });
    }
    if (clientUtils.isNullOrWhitespace(data.title)) {
      errors.push({ fieldId: 'title', message: 'Title is required' });
    }
    if (data.publicationDate == null) {
      errors.push({ fieldId: 'publicationDate', message: 'Publication Date is required' });
    }
    if (data.schemaType == null) {
      errors.push({ fieldId: 'schemaType', message: 'Media Type is required' });
    }

    if (ouData.metaData?.allowMultipleCategories === false) {
      if (_.isArray(data?.common?.categories) && data?.common?.categories?.length > 1) {
        errors.push({ fieldId: 'common.categories', message: 'Only one category is allowed.' });
      }
    }

    if (data.source === AssetSource.ExternalClip) {
      if (clientUtils.isNullOrWhitespace(data.externalHlsFileUrl)) {
        errors.push({ fieldId: 'externalHlsFileUrl', message: 'External HLS URL is required' });
      }
      if (!clientUtils.isValidM3u8Url(data.externalHlsFileUrl)) {
        errors.push({
          fieldId: 'externalHlsFileUrl',
          message: 'External HLS URL must be a valid m3u8 url. Example: https://example.com/hls/index.m3u8',
        });
      }
    }
    if (data.source === AssetSource.ClipGroup) {
      if (data.clipIds.length === 0) {
        errors.push({ fieldId: 'clipIds', message: 'Atleast one clip is required to create Clip Group.' });
      }
    }
    return errors;
  };

  const createAssetForm = async (id: string): Promise<AssetForm> => {
    // console.log('createAssetForm', { id });
    try {
      let asset = await FuelPortalService.getClipById(id);
      asset = enhanceFetchedAsset(asset, permissions, flags, false, [], [], ouData);
      if (asset) {
        const form = await AssetForm.createFromData(asset, permissions, ouData, false, flags);
        // dispatch(UpdateAssetForm(form));
        return form;
      }
      return null;
    } catch (ex) {
      alert('Error fetching asset.', 'error');
      console.error('Error fetching asset', ex);
      return null;
    }
  };

  const onSave = async () => {
    if (loading) {
      return;
    }
    let data = getValues();
    const validationErrors = validateAssetForm(ouData, data);
    // console.log('validationErrors', { validationErrors });
    if (validationErrors.length > 0) {
      alert(validationErrors[0].message, 'error');
      return;
    }
    try {
      if (data.source === AssetSource.ClipGroup) {
        setProgress(true, 'Saving Clip Group...');
        const clipGroupReq = AssetForm.getClipGroup(data, ouData, null);
        const clipGroup = await FuelPortalService.upsertEntity(FuelEntityType.ClipGroup, clipGroupReq);
        const payload = toSaveAssetPayload(data, ouData.schemas, ouData.categories, fuelUserAccessData, ouData);
        if (data.mode === 'new') {
          const oldAssetForm = _.cloneDeep(data);
          const clipGroupAssetForm = await createAssetForm(clipGroup.id);
          const newAssetForm = generateNewAssetForm(oldAssetForm, clipGroupAssetForm);
          // dispatch(UpdateAssetForm(newAssetForm));
          reset(newAssetForm);
          data = newAssetForm;
        }
      }
      setProgress(true, 'Saving Asset...');
      const payloadRequest = toSaveAssetPayload(data, ouData.schemas, ouData.categories, fuelUserAccessData, ouData);
      // console.log('data', { data, payloadRequest });
      const response = await FuelPortalService.updateAsset(payloadRequest);
      if (response && addToList) {
        addToList(response);
        onClose();
        alert('Asset successfully saved.');
      }
    } catch (e) {
      console.error('Unable to save asset', { e });
    } finally {
      setProgress(false);
    }
  };
  const onCancel = () => {
    onClose();
  };

  // const isSuperAdmin = useMemo(() => permissions && permissions.has(Permission.OUSuperAdmin), [permissions]);
  // const schemaType = watch('schemaType');

  const base = 'body';
  const expanded = 'body expanded';

  const handleTabChange = async (event: SyntheticEvent, value: string) => {
    setTab(value);
  };

  useEffect(() => {
    if (assetForm) {
      reset(assetForm);
      setSelectionType(assetForm.schemaType === SchemaType.Video ? 'video' : 'image');
      setProgress(false);
      setIsInitialized(true);
      setIsNew(assetForm.mode === 'new');
    } else {
      setProgress(true);
    }
  }, [assetForm]);

  if (!isInitialized) {
    return null;
  }

  // check MoviedEdit for an updated version
  const onUploadThumbnail = async (file: File) => {
    const allowedFiles = ['.JPEG', '.jpeg', '.JPG', '.jpg', '.PNG', '.png', '.GIF', '.gif'];
    const regex = new RegExp(`([\\W\\S])+(${allowedFiles.join('|')})$`);
    const message = allowedFiles.join(', ');
    const bodyText = `Please upload files having extensions: ${message} only.`;
    if (file && !regex.test(file && file.name.toLowerCase())) {
      alert(bodyText, 'error');
      return;
    }

    const imgBlob = await cliptoolUtils.getFileImageBlob(file);

    const frozenForm: AssetForm = cloneDeep(assetForm);
    frozenForm.ingestFile = file;
    // console.log('onUploadThumbnail', { frozenForm });
    dispatch(CreatAssetForm(frozenForm));
    await dispatch(startJob({ form: frozenForm, jobType: 'Thumbnail' }));

    // optimistic update
    setValue('originalAssetData.data.thumbnail.tempThumbnailBlob', imgBlob);
  };

  return (
    <>
      <VnModalBody>
        <div className="vn-asset-editor">
          <div className="fullHeight">
            <div className="tab-header-container">
              <Box sx={{ borderBottom: 0, borderColor: 'divider' }}>
                <Tabs value={tab} onChange={handleTabChange} aria-label="series tabs">
                  <Tab label="Details" value="details" />
                  {isClipGroup && <Tab label="Clips" value="content-clips" />}
                  <Tab label="Access" value="access-schedule" />

                  {/* <Tab label="Share" value="share" /> */}
                </Tabs>
              </Box>
            </div>
            <FormProvider {...methods}>
              <div className="tab-body-container">
                {tab === 'details' && (
                  <div className={expanded}>
                    <div className="column left">
                      {/* <VNAssetForm /> */}
                      <Preview videoId="player1" type={selectionType as 'video' | 'image'} />
                      <VideoForm isMultiple={false} isNew={isNew} />
                    </div>
                    <div className="column right">
                      <DisplaySettingsForm type={selectionType as 'video' | 'image'} uploadThumbnail={onUploadThumbnail} />
                    </div>
                  </div>
                )}
                {tab === 'access-schedule' && <VNAccessSettings name="accessSettings" />}
                {tab === 'content-clips' && isClipGroup && <ContentClipsTab />}
              </div>
            </FormProvider>
          </div>
        </div>
      </VnModalBody>
      <VnModalFooter
        right={
          <>
            <Button variant="contained" color="success" onClick={onSave}>
              Save
            </Button>
            <Button variant="contained" sx={{ mr: 1 }} onClick={onCancel}>
              Cancel
            </Button>
          </>
        }
      />
    </>
  );
};

export default VNAssetEditor;
